
export enum CancelReason {
  USER_REQUEST = 'USER_REQUEST',
  MAINTENANCE = 'MAINTENANCE',
  USER_INITIATED = 'USER_INITIATED',
  VACATION = 'VACATION',
  PRIVATE_EVENT = 'PRIVATE_EVENT',
  TOURNAMENT = 'TOURNAMENT',
  BAD_WEATHER = 'BAD_WEATHER',
}

export type CourtModel = {
  id?: number
  name: string
  club: ClubModel
  courtType: CourtType
  notes?: string
  lightSwitchName?: string
}

export type CourtModelMetadata = {
  id: number
  name: string
  clubId: number
  clubName: string
  clubAddress?: AddressModel
  type: CourtType
  openTime: string
  closeTime: string
  lightSwitchName?: string
}

/** Booking settings of a club (start-time grid, offered durations, reschedule and pass extension policies). */
export type ClubBookingSettings = {
  startIncrementMinutes: number
  sessionDurations: number[]
  maxReschedules: number
  passExtensionDays: number
  timeZone?: string
}

export type ClubModel = ClubBookingSettings & {
  id?: number
  name: string
  openTime: string
  closeTime: string
  freeCancellation: number
  address: AddressModel
}

export type ClubModelMetadata = Partial<ClubBookingSettings> & {
  id: number
  name: string
  openTime: string
  closeTime: string
  freeCancellation: number
  courtsCount: number
  address: AddressModel
}

export type AddressModel = {
  id?: number
  county: string
  city: string
  street: string
  number: string
  floor?: string
  apartment?: string
  postalCode: string
}

export enum BlockReason {
  MAINTENANCE = 'MAINTENANCE',
  VACATION = 'VACATION',
  PRIVATE_EVENT = 'PRIVATE_EVENT',
  TOURNAMENT = 'TOURNAMENT',
  BAD_WEATHER = 'BAD_WEATHER',
}

/** Block list filter: upcoming blocks have not ended yet, passed ones have. */
export enum BlockFilter {
  UPCOMING = 'UPCOMING',
  PASSED = 'PASSED',
  ALL = 'ALL',
}

export interface BlockModel {
  id?: number
  courtId: number
  /** Create only: every court to block with the same interval and reason (courtId is the first one). */
  courtIds?: number[]
  courtName?: string
  startTime: string
  endTime: string
  reason: string
  by?: string
  notes?: string
}

export type BlockListProps = {
  /** Toggled to reload the list (e.g. after the calendar changed). */
  refreshTrigger?: boolean
  onChanged: () => void | Promise<void>
}

export interface PartnerModel {
  id: number
  firstName: string
  lastName: string
  email: string
  status: PartnerStatus
  favorite: boolean
  gamesPlayed: number
  winRate: number
}

export enum PartnerStatus {
  REQUEST_SENT = 'REQUEST_SENT',
  REQUEST_RECEIVED = 'REQUEST_RECEIVED',
  LINKED = 'LINKED',
  BLOCKED = 'BLOCKED',
}

export enum CourtType {
  INDOOR = 'INDOOR',
  OUTDOOR = 'OUTDOOR',
}

export type StatisticValueMetadata = {
  value: number
  changePercentage: number | null
}

export type TopCourtReportModel = {
  courtName: string
  reservations: number
  fillRate: number
}

export type RevenueSplitModel = {
  memberships: number
  payPerPlay: number
}

export enum BookingRecipientType {
  EXISTING_PROFILE = 'EXISTING_USER',
  NEW_PROFILE = 'NEW_USER',
  GUEST_USER = 'GUEST_USER',
}

export type ExistingUserRecipient = {
  type: BookingRecipientType.EXISTING_PROFILE
  profileId: string
}

export type NewUserRecipient = {
  type: BookingRecipientType.NEW_PROFILE
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
}

export type GuestUserRecipient = {
  type: BookingRecipientType.GUEST_USER
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string
  address: AddressModel
}

export type AccountCreationRequest = {
  password: string
  termsConsent: boolean
}

export type GuestBookingRecipient = GuestUserRecipient & {
  accountCreation?: AccountCreationRequest
}

export type BookingRecipient =
  | ExistingUserRecipient
  | NewUserRecipient
  | GuestUserRecipient

/** A booking that a planned block would displace (or a hold in progress, which makes the block wait). */
export type BlockConflict = {
  id: number
  bookingStartTime: string
  bookingEndTime?: string
  courtName: string
  bookedBy: string
  partnerName?: string | null
  status: string
}

export type LightSwitchState = {
  name: string
  enabled: boolean
  reachable: boolean
}
